class Tooltip {
  constructor(element) {
    this.element = element;

    this.trigger = element.querySelector(
      "[data-tooltip-trigger]",
    );

    this.content = element.querySelector(
      "[data-tooltip-content]",
    );

    this.heading = element.querySelector(
      ".tooltip__heading",
    );

    this.preferredPosition =
      element.dataset.tooltipPosition || "top";

    this.viewportGutter = 12;
    this.offset = 8;

    this.handleMouseEnter =
      this.handleMouseEnter.bind(this);

    this.handleMouseLeave =
      this.handleMouseLeave.bind(this);

    this.handleFocusIn =
      this.handleFocusIn.bind(this);

    this.handleFocusOut =
      this.handleFocusOut.bind(this);

    this.handleTriggerPointerDown =
      this.handleTriggerPointerDown.bind(this);

    this.handleTriggerClick =
      this.handleTriggerClick.bind(this);

    this.handleDocumentClick =
      this.handleDocumentClick.bind(this);

    this.handleViewportChange =
      this.handleViewportChange.bind(this);

    this.handleKeyDown =
      this.handleKeyDown.bind(this);

    this.init();
  }

  init() {
    if (!this.trigger || !this.content) {
      return;
    }

    // Touch browsers synthesize a mouseenter right before the click on a
    // tap, which would open() then immediately have the click handler
    // read "already open" and close() it again. Only wire up hover on
    // devices that actually have a hover-capable pointer; touch relies on
    // the click-to-toggle handler below instead.
    if (window.matchMedia?.("(hover: hover)").matches) {
      this.element.addEventListener(
        "mouseenter",
        this.handleMouseEnter,
      );

      this.element.addEventListener(
        "mouseleave",
        this.handleMouseLeave,
      );
    }

    this.element.addEventListener(
      "focusin",
      this.handleFocusIn,
    );

    this.element.addEventListener(
      "focusout",
      this.handleFocusOut,
    );

    // Android Chrome also focuses a <button> on tap, ahead of the
    // synthesized click — without this guard, focusin would open() it,
    // then the click would immediately see "already open" and close() it
    // in the same tap. A pointerdown right before focus marks the focus
    // as pointer-driven so handleFocusIn can defer to the click handler.
    this.trigger.addEventListener(
      "pointerdown",
      this.handleTriggerPointerDown,
    );

    this.trigger.addEventListener(
      "click",
      this.handleTriggerClick,
    );

    // Touch devices have no hover, so the visible label should toggle the
    // tooltip too rather than only the small icon.
    this.heading?.addEventListener(
      "click",
      this.handleTriggerClick,
    );

    document.addEventListener(
      "click",
      this.handleDocumentClick,
    );

    document.addEventListener(
      "keydown",
      this.handleKeyDown,
    );

    window.addEventListener(
      "resize",
      this.handleViewportChange,
    );

    window.addEventListener(
      "scroll",
      this.handleViewportChange,
      true,
    );
  }

  handleMouseEnter() {
    this.open();
  }

  handleMouseLeave() {
    this.close();
  }

  handleFocusIn() {
    if (this.suppressFocusOpen) {
      this.suppressFocusOpen = false;
      return;
    }

    this.open();
  }

  handleFocusOut(event) {
    if (!this.element.contains(event.relatedTarget)) {
      this.close();
    }
  }

  handleTriggerPointerDown() {
    this.suppressFocusOpen = true;
  }

  handleTriggerClick(event) {
    event.preventDefault();

    if (this.element.classList.contains("is-open")) {
      this.close();
      return;
    }

    this.open();
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) {
      this.close();
    }
  }

  handleKeyDown(event) {
    if (event.key !== "Escape") {
      return;
    }

    this.close();
    this.trigger.focus();
  }

  handleViewportChange() {
    if (this.element.classList.contains("is-open")) {
      this.position();
    }
  }

  open() {
    this.element.classList.add("is-open");

    this.trigger.setAttribute(
      "aria-expanded",
      "true",
    );

    requestAnimationFrame(() => {
      this.position();
    });
  }

  close() {
    this.element.classList.remove("is-open");

    this.trigger.setAttribute(
      "aria-expanded",
      "false",
    );
  }

  position() {
    const triggerRect =
      this.trigger.getBoundingClientRect();

    const contentRect =
      this.content.getBoundingClientRect();

    const availableSpace = {
      top: triggerRect.top,
      right: window.innerWidth - triggerRect.right,
      bottom: window.innerHeight - triggerRect.bottom,
      left: triggerRect.left,
    };

    const requiredSpace = {
      top: contentRect.height + this.offset,
      right: contentRect.width + this.offset,
      bottom: contentRect.height + this.offset,
      left: contentRect.width + this.offset,
    };

    let position = this.preferredPosition;

    if (
      availableSpace[position] <
      requiredSpace[position]
    ) {
      position = this.getBestPosition(
        availableSpace,
        requiredSpace,
      );
    }

    const coordinates = this.getCoordinates(
      position,
      triggerRect,
      contentRect,
    );

    const x = this.clamp(
      coordinates.x,
      this.viewportGutter,
      window.innerWidth -
        contentRect.width -
        this.viewportGutter,
    );

    const y = this.clamp(
      coordinates.y,
      this.viewportGutter,
      window.innerHeight -
        contentRect.height -
        this.viewportGutter,
    );

    this.updatePlacementClass(position);

    const containingBlockOffset = this.getFixedContainingBlockOffset();

    this.content.style.setProperty(
      "--tooltip-x",
      `${Math.round(x - containingBlockOffset.x)}px`,
    );

    this.content.style.setProperty(
      "--tooltip-y",
      `${Math.round(y - containingBlockOffset.y)}px`,
    );

    this.updateArrowPosition(
      position,
      triggerRect,
      contentRect,
      x,
      y,
    );
  }

  getBestPosition(availableSpace, requiredSpace) {
    const positions = [
      this.preferredPosition,
      this.getOppositePosition(
        this.preferredPosition,
      ),
      "top",
      "bottom",
      "right",
      "left",
    ];

    const uniquePositions = [...new Set(positions)];

    const fittingPosition = uniquePositions.find(
      (position) =>
        availableSpace[position] >=
        requiredSpace[position],
    );

    if (fittingPosition) {
      return fittingPosition;
    }

    return uniquePositions.reduce(
      (best, position) =>
        availableSpace[position] >
        availableSpace[best]
          ? position
          : best,
      uniquePositions[0],
    );
  }

  getCoordinates(
    position,
    triggerRect,
    contentRect,
  ) {
    const triggerCenterX =
      triggerRect.left + triggerRect.width / 2;

    const triggerCenterY =
      triggerRect.top + triggerRect.height / 2;

    switch (position) {
      case "bottom":
        return {
          x: triggerCenterX - contentRect.width / 2,
          y: triggerRect.bottom + this.offset,
        };

      case "left":
        return {
          x:
            triggerRect.left -
            contentRect.width -
            this.offset,
          y: triggerCenterY - contentRect.height / 2,
        };

      case "right":
        return {
          x: triggerRect.right + this.offset,
          y: triggerCenterY - contentRect.height / 2,
        };

      case "top":
      default:
        return {
          x: triggerCenterX - contentRect.width / 2,
          y:
            triggerRect.top -
            contentRect.height -
            this.offset,
        };
    }
  }

  updateArrowPosition(
    position,
    triggerRect,
    contentRect,
    x,
    y,
  ) {
    let arrowPosition;

    if (position === "top" || position === "bottom") {
      const triggerCenter =
        triggerRect.left + triggerRect.width / 2;

      arrowPosition = this.clamp(
        triggerCenter - x,
        12,
        contentRect.width - 12,
      );
    } else {
      const triggerCenter =
        triggerRect.top + triggerRect.height / 2;

      arrowPosition = this.clamp(
        triggerCenter - y,
        12,
        contentRect.height - 12,
      );
    }

    this.content.style.setProperty(
      "--tooltip-arrow-position",
      `${Math.round(arrowPosition)}px`,
    );
  }

  updatePlacementClass(position) {
    this.element.classList.remove(
      "tooltip--top",
      "tooltip--right",
      "tooltip--bottom",
      "tooltip--left",
    );

    this.element.classList.add(
      `tooltip--${position}`,
    );
  }

  getOppositePosition(position) {
    const oppositePositions = {
      top: "bottom",
      right: "left",
      bottom: "top",
      left: "right",
    };

    return oppositePositions[position] || "bottom";
  }

  clamp(value, minimum, maximum) {
    return Math.min(
      Math.max(value, minimum),
      Math.max(minimum, maximum),
    );
  }

  getFixedContainingBlockOffset() {
    let node = this.content.parentElement;

    while (node && node !== document.documentElement) {
      const style = window.getComputedStyle(node);

      const establishesContainingBlock =
        (style.transform && style.transform !== "none") ||
        (style.filter && style.filter !== "none") ||
        (style.backdropFilter && style.backdropFilter !== "none") ||
        (style.perspective && style.perspective !== "none") ||
        (style.willChange &&
          (style.willChange.includes("transform") ||
            style.willChange.includes("perspective")));

      if (establishesContainingBlock) {
        const rect = node.getBoundingClientRect();

        return { x: rect.left, y: rect.top };
      }

      node = node.parentElement;
    }

    return { x: 0, y: 0 };
  }

  destroy() {
    this.close();

    this.element.removeEventListener(
      "mouseenter",
      this.handleMouseEnter,
    );

    this.element.removeEventListener(
      "mouseleave",
      this.handleMouseLeave,
    );

    this.element.removeEventListener(
      "focusin",
      this.handleFocusIn,
    );

    this.element.removeEventListener(
      "focusout",
      this.handleFocusOut,
    );

    this.trigger?.removeEventListener(
      "pointerdown",
      this.handleTriggerPointerDown,
    );

    this.trigger?.removeEventListener(
      "click",
      this.handleTriggerClick,
    );

    this.heading?.removeEventListener(
      "click",
      this.handleTriggerClick,
    );

    document.removeEventListener(
      "click",
      this.handleDocumentClick,
    );

    document.removeEventListener(
      "keydown",
      this.handleKeyDown,
    );

    window.removeEventListener(
      "resize",
      this.handleViewportChange,
    );

    window.removeEventListener(
      "scroll",
      this.handleViewportChange,
      true,
    );
  }
}

export const initializeTooltips = (
  container = document,
) => {
  const elements = [];

  if (container.matches?.("[data-tooltip]")) {
    elements.push(container);
  }

  elements.push(
    ...container.querySelectorAll("[data-tooltip]"),
  );

  elements.forEach((element) => {
    if (element._tooltip) {
      element._tooltip.destroy();
    }

    element._tooltip = new Tooltip(element);
  });
};

initializeTooltips();

document.addEventListener(
  "shopify:section:load",
  (event) => {
    initializeTooltips(event.target);
  },
);

export default Tooltip;